import { UIElement } from '../ui-element';
import {
    createElement,
    setAttributes,
    setInnerText,
    updateClassList,
} from '../../global/render-api';
import { browser } from '../../global/browser';
import { TabIndex } from '../../global/keyboard';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type {
    AttributeValue,
    ElementConfig,
    ObservedAttributes,
} from '../ui-element.type';
import type { NullOr } from '../../global/utils.type';
import styles from './ui-navlist-menu.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UINavListMenu
 * @element ui-navlist-menu
 * @property {string} [label] {@attr label} Title for the menu.
 * @property {HTMLHeadingElement} caption Read-only shortcut to caption heading.
 * @classdesc Represents a class for <code>ui-navlist-menu</code> element
 * @example
 * <ui-navlist-menu></ui-navlist-menu>
 */
@Component({ tag: 'ui-navlist-menu', styles })
export class UINavListMenu extends UIElement {
    @Attribute(String) label?: string;
    @Child('.ui-navlist-menu__caption') caption?: HTMLHeadingElement;

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['label'];
    }

    /**
     * Sets caption value from the caption attribute
     */
    updateCaption(): void {
        if (!this.caption) {
            const newCaption = createElement<HTMLHeadingElement>({
                tagName: 'h3',
                classList: {
                    'ui-navlist-menu__caption': true,
                },
            });
            this.insertBefore(newCaption!, this.firstChild);
        }
        setInnerText(this.caption!, this.label!);
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }
        switch (name) {
            case 'label':
                this.updateCaption();
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @private
     * @param {number} tabIndex
     */
    private setTabIndex(tabIndex: number): void {
        if (Number(TabIndex.Active) === tabIndex) {
            this.setAttribute('tabindex', String(tabIndex));
        } else {
            this.removeAttribute('tabindex');
        }
    }

    private tabindexHandlerSetter!: () => void;
    private tabindexHandlerRemover!: () => void;
    /**
     * @private
     */
    private subscribeEvents(): void {
        browser.mobileMedia.addEventListener(
            'change',
            this.tabindexHandlerSetter
        );
        browser.desktopMedia.addEventListener(
            'change',
            this.tabindexHandlerRemover
        );
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        browser.mobileMedia.removeEventListener(
            'change',
            this.tabindexHandlerSetter
        );
        browser.desktopMedia.removeEventListener(
            'change',
            this.tabindexHandlerRemover
        );
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        setAttributes(this, {
            'role': 'menu',
            'aria-label': this.label ?? null,
            'tabindex': browser.mobileMediaMatches() ? TabIndex.Active : null,
        });
        updateClassList(this, { '-secondary': true });
        this.insertElements([
            ...(this.label
                ? [
                      {
                          tagName: 'h3',
                          classList: {
                              'ui-navlist-menu__caption': true,
                          },
                          children: [this.label],
                      } as ElementConfig,
                  ]
                : []),
            {
                tagName: 'div',
                classList: {
                    'ui-navlist-menu__nav': true,
                },
                children: [
                    {
                        tagName: 'ul',
                        classList: {
                            'ui-navlist-menu__items': true,
                        },
                        children: this.childNodes,
                    },
                ],
            },
        ]);
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.tabindexHandlerSetter = this.setTabIndex.bind(
            this,
            Number(TabIndex.Active)
        );
        this.tabindexHandlerRemover = this.setTabIndex.bind(
            this,
            Number(TabIndex.Inactive)
        );
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
    }
}
